import { useState, useRef } from 'react';
import styled from 'styled-components';
import { mqLargeMin } from 'styled/tools/media-queries';

const Annotation = styled.span`
  @media ${mqLargeMin} {
    position: relative;
  }

  * {
    color: var(--text-primary);
  }
`;

const AnnotationTitle = styled.span`
  background-color: var(--guidance-secondary);
  background-size: 200% 100%;
  background-position: right bottom;
  transition: background-position 800ms ease;
  cursor: pointer;
  padding: 5px 0;

  &.animated {
    background-position: left bottom;
  }

  &:before,
  &:after {
    content: ' ';
    background-color: white;
    padding: 5px 0;
  }
`;

const AnnotationCard = styled.div`
  position: absolute;
  pointer-events: none;
  width: 300px;
  background-color: var(--guidance-secondary);
  border-radius: 8px;
  left: 50%;
  transform: translateX(-50%);
  max-height: 0px;
  transition: max-height 0.25s ease;
  overflow: hidden;
  box-shadow: 0px 0px 12px #bbc3b8;

  @media ${mqLargeMin} {
    left: 0;
    transform: translateX(0);
  }

  &.visible {
    pointer-events: initial;
  }
`;

const Container = styled.div`
  padding: 25px;
`;

const AnnotationDot = styled.a`
  height: 25px;
  margin-bottom: 20px;
  justify-content: center;
`;

const AnnotationClose = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

const AnnotationButton = styled.button`
  font-size: 13px;
  font-weight: 400;
  color: var(--text-primary);
  opacity: 0.4;
  background: none;
  border: none;
  cursor: pointer;
`;

const SiteAnnotation = ({ title, visible, className, children }) => {
  const [open, setOpen] = useState(false);
  const [maxHeight, setMaxHeight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(undefined);
  const classes = ['site-annotation'].concat(className).join(' ');

  const handleClick = () => {
    const containerOverflow =
      containerRef.current.getBoundingClientRect().bottom - window.innerHeight;

    if (!open && containerOverflow > 0) {
      window.scroll({
        top: window.scrollY + containerOverflow,
        behavior: 'smooth',
      });
    }

    setMaxHeight(() => {
      if (open) {
        setOpen(false);
        return 0;
      }
      setOpen(true);

      return containerRef.current.getBoundingClientRect().height;
    });
  };

  return (
    <Annotation className={classes} onClick={handleClick}>
      <AnnotationTitle className={`${visible ? 'animated' : ''}`}>
        {title}
      </AnnotationTitle>
      <AnnotationCard
        className={`${open ? 'visible' : ''}`}
        style={{ maxHeight }}
      >
        <Container className='container' ref={containerRef}>
          <AnnotationDot className='site-dot'>
            <i />
          </AnnotationDot>
          {children && children}
          <AnnotationClose>
            <AnnotationButton onClick={handleClick} type='button'>
              Close
            </AnnotationButton>
          </AnnotationClose>
        </Container>
      </AnnotationCard>
    </Annotation>
  );
};
export default SiteAnnotation;
